import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

const API_URL = '/api';

// Configure axios to include auth token
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Navigation tabs
const TABS = {
  DASHBOARD: 'dashboard',
  QUESTIONS: 'questions',
  AREAS: 'areas',
  EXAMS: 'exams',
  IMPORT: 'import',
  SETTINGS: 'settings'
};

// Public Exam Page Component (no auth required)
function PublicExamPage({ token }) {
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadExam();
  }, [token]);

  const loadExam = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/exam/${token}`);
      setExam(response.data);
      // Initialize answers
      const initialAnswers = {};
      response.data.questions.forEach(q => {
        if (q.question_type === 'free_text') {
          initialAnswers[q.id] = { text_answer: '' };
        } else {
          initialAnswers[q.id] = { selected_options: [] };
        }
      });
      setAnswers(initialAnswers);
    } catch (err) {
      setError(err.response?.data?.error || 'Prüfung nicht gefunden');
    }
    setLoading(false);
  };

  const handleOptionChange = (questionId, optionText, questionType) => {
    setAnswers(prev => {
      const current = prev[questionId] || { selected_options: [] };
      let selected = [...(current.selected_options || [])];
      
      if (questionType === 'single_choice') {
        selected = [optionText];
      } else {
        if (selected.includes(optionText)) {
          selected = selected.filter(o => o !== optionText);
        } else {
          selected.push(optionText);
        }
      }
      
      return { ...prev, [questionId]: { selected_options: selected } };
    });
  };

  const handleTextChange = (questionId, text) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { text_answer: text }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!participantName.trim()) {
      alert('Bitte geben Sie Ihren Namen ein.');
      return;
    }
    
    setSubmitting(true);
    try {
      const answerArray = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: parseInt(questionId),
        ...answer
      }));
      
      await axios.post(`${API_URL}/public/exam/${token}/submit`, {
        participant_name: participantName,
        answers: answerArray
      });
      
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Einreichen');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Lade Prüfung...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-gray-800">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <div className="text-green-600 text-6xl mb-4">✓</div>
          <h1 className="text-2xl font-bold mb-4">Erfolgreich eingereicht!</h1>
          <p className="text-gray-600">Vielen Dank für Ihre Teilnahme, {participantName}.</p>
          <p className="text-gray-500 mt-2">Ihre Antworten wurden gespeichert.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold mb-2">{exam.title}</h1>
          {exam.description && <p className="text-gray-600 mb-4">{exam.description}</p>}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">Ihr Name *</label>
            <input
              type="text"
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Vor- und Nachname"
              required
            />
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {exam.questions.map((question, idx) => (
            <div key={question.id} className="bg-white rounded-lg shadow-lg p-6 mb-4">
              <div className="flex items-start gap-3 mb-4">
                <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {idx + 1}
                </span>
                <p className="font-medium text-lg">{question.question_text}</p>
              </div>

              {question.question_type === 'free_text' ? (
                <textarea
                  value={answers[question.id]?.text_answer || ''}
                  onChange={(e) => handleTextChange(question.id, e.target.value)}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[120px]"
                  placeholder="Ihre Antwort..."
                />
              ) : (
                <div className="space-y-2">
                  {(question.answer_options || question.options || []).map((option, optIdx) => {
                    const isSelected = answers[question.id]?.selected_options?.includes(option.option_text);
                    const inputType = question.question_type === 'single_choice' ? 'radio' : 'checkbox';
                    
                    return (
                      <label
                        key={optIdx}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type={inputType}
                          name={`question_${question.id}`}
                          checked={isSelected}
                          onChange={() => handleOptionChange(question.id, option.option_text, question.question_type)}
                          className="w-5 h-5"
                        />
                        <span>{option.option_text}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          <div className="bg-white rounded-lg shadow-lg p-6">
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-green-600 text-white text-lg font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? 'Wird eingereicht...' : '📤 Prüfung einreichen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Login Component
function LoginForm({ onLogin, showMessage }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { username, password });
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
    } catch (error) {
      showMessage(error.response?.data?.error || 'Login fehlgeschlagen', 'error');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <h1 className="text-2xl font-bold text-center mb-6">📝 Fragenpool Login</h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Benutzername</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Anmelden...' : 'Anmelden'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Main App Router - handles public vs admin routes
function App() {
  // Check for public exam URL first
  const path = window.location.pathname;
  const publicExamMatch = path.match(/^\/pruefung\/([a-zA-Z0-9-]+)$/);
  
  // If it's a public exam URL, render that page directly (no auth needed)
  if (publicExamMatch) {
    return <PublicExamPage token={publicExamMatch[1]} />;
  }
  
  // Otherwise render the admin app
  return <AdminApp />;
}

function AdminApp() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.DASHBOARD);
  const [stats, setStats] = useState(null);
  const [areas, setAreas] = useState([]);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [pendingItem, setPendingItem] = useState(null);

  const navigateToItem = (tab, item) => {
    setPendingItem(item);
    setActiveTab(tab);
  };

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/verify`);
          setUser(response.data.user);
        } catch (error) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
      setAuthChecked(true);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      const initialLoad = async () => {
        setLoading(true);
        await loadData();
        setLoading(false);
      };
      initialLoad();
    }
  }, [user]);

  const handleLogout = async () => {
    await axios.post(`${API_URL}/auth/logout`);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const loadData = async () => {
    try {
      const [statsRes, areasRes, sectionsRes, questionsRes, examsRes] = await Promise.all([
        axios.get(`${API_URL}/stats`),
        axios.get(`${API_URL}/areas`),
        axios.get(`${API_URL}/sections`),
        axios.get(`${API_URL}/questions`),
        axios.get(`${API_URL}/exams`)
      ]);
      setStats(statsRes.data);
      setAreas(areasRes.data);
      setSections(sectionsRes.data);
      setQuestions(questionsRes.data);
      setExams(examsRes.data);
    } catch (error) {
      showMessage('Fehler beim Laden der Daten', 'error');
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Show loading while checking auth
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Laden...</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <LoginForm onLogin={setUser} showMessage={showMessage} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">📝 Fragenpool - Prüfungsbogen Generator</h1>
          <div className="flex items-center gap-4">
            <span>👤 {user.username}</span>
            <button onClick={handleLogout} className="px-3 py-1 bg-blue-500 rounded hover:bg-blue-400">
              Abmelden
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="container mx-auto px-4">
          <div className="flex space-x-4">
            {Object.entries({
              [TABS.DASHBOARD]: '📊 Dashboard',
              [TABS.QUESTIONS]: '❓ Fragen',
              [TABS.AREAS]: '📁 Bereiche',
              [TABS.EXAMS]: '📄 Prüfungsbögen',
              [TABS.IMPORT]: '📥 Import',
              [TABS.SETTINGS]: '⚙️ Einstellungen'
            }).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-3 font-medium transition-colors ${
                  activeTab === key
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Message */}
      {message && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          message.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white`}>
          {message.text}
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {activeTab === TABS.DASHBOARD && (
              <Dashboard stats={stats} questions={questions} exams={exams} onNavigate={setActiveTab} onNavigateToItem={navigateToItem} />
            )}
            {activeTab === TABS.QUESTIONS && (
              <QuestionsTab
                questions={questions}
                areas={areas}
                sections={sections}
                onReload={loadData}
                showMessage={showMessage}
                pendingItem={pendingItem}
                onConsumeItem={() => setPendingItem(null)}
              />
            )}
            {activeTab === TABS.AREAS && (
              <AreasTab
                areas={areas}
                sections={sections}
                onReload={loadData}
                showMessage={showMessage}
              />
            )}
            {activeTab === TABS.EXAMS && (
              <ExamsTab
                exams={exams}
                areas={areas}
                sections={sections}
                questions={questions}
                onReload={loadData}
                showMessage={showMessage}
                pendingItem={pendingItem}
                onConsumeItem={() => setPendingItem(null)}
              />
            )}
            {activeTab === TABS.IMPORT && (
              <ImportTab onReload={loadData} showMessage={showMessage} />
            )}
            {activeTab === TABS.SETTINGS && (
              <SettingsTab showMessage={showMessage} />
            )}
          </>
        )}
      </main>
    </div>
  );
}

// Dashboard Component
function Dashboard({ stats, questions, exams, onNavigate, onNavigateToItem }) {
  const [recentSubmissions, setRecentSubmissions] = useState([]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const res = await axios.get(`${API_URL}/submissions/recent`);
        setRecentSubmissions(res.data);
      } catch (e) {
        console.error('Error fetching recent submissions:', e);
      }
    };
    fetchSubmissions();
  }, []);

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Dashboard</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <button onClick={() => onNavigate(TABS.QUESTIONS)} className="text-left">
          <StatCard title="Fragen" value={stats?.question_count || 0} icon="❓" color="blue" />
        </button>
        <button onClick={() => onNavigate(TABS.AREAS)} className="text-left">
          <StatCard title="Bereiche" value={stats?.area_count || 0} icon="📁" color="green" />
        </button>
        <button onClick={() => onNavigate(TABS.AREAS)} className="text-left">
          <StatCard title="Abschnitte" value={stats?.section_count || 0} icon="📑" color="yellow" />
        </button>
        <button onClick={() => onNavigate(TABS.EXAMS)} className="text-left">
          <StatCard title="Prüfungsbögen" value={stats?.exam_count || 0} icon="📄" color="purple" />
        </button>
      </div>

      {/* Recent items */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Letzte Fragen</h3>
            <button onClick={() => onNavigate(TABS.QUESTIONS)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Alle anzeigen →</button>
          </div>
          {questions.length === 0 ? (
            <p className="text-gray-500 text-sm italic">Keine Fragen vorhanden</p>
          ) : (
            questions.slice(0, 5).map(q => (
              <button
                key={q.id}
                onClick={() => onNavigateToItem(TABS.QUESTIONS, { type: 'question', id: q.id })}
                className="w-full text-left py-2 border-b last:border-0 hover:bg-gray-50 rounded px-2 transition-colors"
              >
                <p className="text-sm truncate font-medium">{q.question_text}</p>
                <div className="flex gap-2 mt-0.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    q.question_type === 'multiple_choice' ? 'bg-purple-100 text-purple-700' :
                    q.question_type === 'single_choice' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {q.question_type === 'multiple_choice' ? 'MC' :
                     q.question_type === 'single_choice' ? 'SC' : 'Freitext'}
                  </span>
                  <span className="text-xs text-gray-500">{q.points} Pkt</span>
                </div>
              </button>
            ))
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Letzte Prüfungsbögen</h3>
            <button onClick={() => onNavigate(TABS.EXAMS)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Alle anzeigen →</button>
          </div>
          {exams.length === 0 ? (
            <p className="text-gray-500 text-sm italic">Keine Prüfungsbögen vorhanden</p>
          ) : (
            exams.slice(0, 5).map(e => (
              <button
                key={e.id}
                onClick={() => onNavigateToItem(TABS.EXAMS, { type: 'exam', id: e.id })}
                className="w-full text-left py-2 border-b last:border-0 hover:bg-gray-50 rounded px-2 transition-colors"
              >
                <p className="text-sm font-medium">{e.title}</p>
                <span className="text-xs text-gray-500">{e.question_count} Fragen</span>
              </button>
            ))
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Letzte Rückläufer</h3>
            <button onClick={() => onNavigate(TABS.EXAMS)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Alle anzeigen →</button>
          </div>
          {recentSubmissions.length === 0 ? (
            <p className="text-gray-500 text-sm italic">Keine Rückläufer vorhanden</p>
          ) : (
            recentSubmissions.slice(0, 5).map(s => {
              const percentage = s.max_points > 0 ? Math.round((s.total_points / s.max_points) * 100) : 0;
              const passed = percentage >= (s.passing_percentage || 50);
              return (
                <button
                  key={s.id}
                  onClick={() => onNavigateToItem(TABS.EXAMS, { type: 'submission', id: s.id, examId: s.exam_id })}
                  className="w-full text-left py-2 border-b last:border-0 hover:bg-gray-50 rounded px-2 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{s.participant_name || 'Anonym'}</p>
                      <p className="text-xs text-gray-500 truncate">{s.exam_title}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <span className={`text-xs font-bold ${passed ? 'text-green-600' : 'text-red-600'}`}>
                        {percentage}%
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {passed ? '✓' : '✗'}
                      </span>
                      {s.is_corrected ? (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">✔</span>
                      ) : (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">⏳</span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(s.submitted_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
    green: 'bg-green-100 text-green-800 hover:bg-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
    purple: 'bg-purple-100 text-purple-800 hover:bg-purple-200'
  };
  return (
    <div className={`${colors[color]} rounded-lg p-6 cursor-pointer transition-colors`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
        </div>
        <span className="text-4xl">{icon}</span>
      </div>
    </div>
  );
}

// Questions Tab
function QuestionsTab({ questions, areas, sections, onReload, showMessage, pendingItem, onConsumeItem }) {
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [filterArea, setFilterArea] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkAction, setBulkAction] = useState('');

  useEffect(() => {
    if (pendingItem?.type === 'question') {
      const q = questions.find(q => q.id === pendingItem.id);
      if (q) {
        setEditingQuestion(q);
        setShowForm(true);
      }
      onConsumeItem();
    }
  }, [pendingItem]);

  const filteredQuestions = questions.filter(q => {
    if (filterArea && !q.areas.some(a => a.id === parseInt(filterArea))) return false;
    if (filterSection && !q.sections.some(s => s.id === parseInt(filterSection))) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesText = q.question_text.toLowerCase().includes(query);
      const matchesOptions = q.answer_options?.some(o => o.option_text.toLowerCase().includes(query));
      if (!matchesText && !matchesOptions) return false;
    }
    return true;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Frage wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/questions/${id}`);
      showMessage('Frage gelöscht');
      onReload();
    } catch (error) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === filteredQuestions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredQuestions.map(q => q.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`${selectedIds.length} Fragen wirklich löschen?`)) return;
    try {
      await axios.post(`${API_URL}/questions/bulk-delete`, { ids: selectedIds });
      showMessage(`${selectedIds.length} Fragen gelöscht`);
      setSelectedIds([]);
      onReload();
    } catch (error) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  const handleBulkUpdate = async (updates) => {
    try {
      await axios.post(`${API_URL}/questions/bulk-update`, { ids: selectedIds, updates });
      showMessage(`${selectedIds.length} Fragen aktualisiert`);
      setSelectedIds([]);
      setShowBulkEdit(false);
      onReload();
    } catch (error) {
      showMessage('Fehler beim Aktualisieren', 'error');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Fragen verwalten</h2>
        <button
          onClick={() => { setEditingQuestion(null); setShowForm(true); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Neue Frage
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4 flex gap-4 flex-wrap">
        <input
          type="text"
          placeholder="🔍 Fragen durchsuchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border rounded px-3 py-2 flex-1 min-w-64"
        />
        <select
          value={filterArea}
          onChange={(e) => setFilterArea(e.target.value)}
          className="border rounded px-3 py-2"
        >
          <option value="">Alle Bereiche</option>
          {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select
          value={filterSection}
          onChange={(e) => setFilterSection(e.target.value)}
          className="border rounded px-3 py-2"
        >
          <option value="">Alle Abschnitte</option>
          {sections.map(s => <option key={s.id} value={s.id}>{s.name} ({s.area_name})</option>)}
        </select>
        {(searchQuery || filterArea || filterSection) && (
          <button
            onClick={() => { setSearchQuery(''); setFilterArea(''); setFilterSection(''); }}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕ Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex gap-4 items-center flex-wrap">
          <span className="font-medium">{selectedIds.length} Fragen ausgewählt</span>
          <button
            onClick={handleBulkDelete}
            className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
          >
            🗑️ Löschen
          </button>
          <button
            onClick={() => { setBulkAction('points'); setShowBulkEdit(true); }}
            className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
          >
            Punkte ändern
          </button>
          <button
            onClick={() => { setBulkAction('replaceAreas'); setShowBulkEdit(true); }}
            className="bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700"
          >
            Bereiche ersetzen
          </button>
          <button
            onClick={() => { setBulkAction('addAreas'); setShowBulkEdit(true); }}
            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
          >
            Bereiche hinzufügen
          </button>
          <button
            onClick={() => { setBulkAction('replaceSections'); setShowBulkEdit(true); }}
            className="bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700"
          >
            Abschnitte ersetzen
          </button>
          <button
            onClick={() => { setBulkAction('addSections'); setShowBulkEdit(true); }}
            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
          >
            Abschnitte hinzufügen
          </button>
          <button
            onClick={() => setSelectedIds([])}
            className="text-gray-600 hover:text-gray-800"
          >
            ✕ Auswahl aufheben
          </button>
        </div>
      )}

      <div className="text-sm text-gray-500 mb-2">{filteredQuestions.length} von {questions.length} Fragen</div>

      {/* Questions List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedIds.length === filteredQuestions.length && filteredQuestions.length > 0}
                  onChange={selectAll}
                  className="w-4 h-4"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Frage</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Typ</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Bereiche</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Punkte</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredQuestions.map(q => (
              <tr key={q.id} className={`hover:bg-gray-50 ${selectedIds.includes(q.id) ? 'bg-blue-50' : ''}`}>
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(q.id)}
                    onChange={() => toggleSelect(q.id)}
                    className="w-4 h-4"
                  />
                </td>
                <td className="px-4 py-3 max-w-md truncate">{q.question_text}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    q.question_type === 'multiple_choice' ? 'bg-purple-100 text-purple-800' :
                    q.question_type === 'single_choice' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {q.question_type === 'multiple_choice' ? 'Mehrfach' :
                     q.question_type === 'single_choice' ? 'Einfach' : 'Freitext'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {q.areas.map(a => (
                    <span key={a.id} className="bg-gray-100 px-2 py-1 rounded text-xs mr-1">{a.name}</span>
                  ))}
                </td>
                <td className="px-4 py-3">{q.points}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => { setEditingQuestion(q); setShowForm(true); }}
                    className="text-blue-600 hover:text-blue-800 mr-2"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(q.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEdit && (
        <BulkEditModal
          action={bulkAction}
          areas={areas}
          sections={sections}
          onClose={() => setShowBulkEdit(false)}
          onSave={handleBulkUpdate}
        />
      )}

      {/* Question Form Modal */}
      {showForm && (
        <QuestionForm
          question={editingQuestion}
          areas={areas}
          sections={sections}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); onReload(); showMessage(editingQuestion ? 'Frage aktualisiert' : 'Frage erstellt'); }}
          showMessage={showMessage}
        />
      )}
    </div>
  );
}

// Bulk Edit Modal
function BulkEditModal({ action, areas, sections, onClose, onSave }) {
  const [points, setPoints] = useState(1);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);

  const handleSubmit = () => {
    const updates = {};
    if (action === 'points') {
      updates.points = points;
    } else if (action === 'replaceAreas') {
      updates.replaceAreas = true;
      updates.areaIds = selectedAreas;
    } else if (action === 'addAreas') {
      updates.addAreas = true;
      updates.addAreaIds = selectedAreas;
    } else if (action === 'replaceSections') {
      updates.replaceSections = true;
      updates.sectionIds = selectedSections;
    } else if (action === 'addSections') {
      updates.addSections = true;
      updates.addSectionIds = selectedSections;
    }
    onSave(updates);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4 p-6">
        <h3 className="text-xl font-bold mb-4">
          {action === 'points' && 'Punkte ändern'}
          {action === 'replaceAreas' && 'Bereiche ersetzen'}
          {action === 'addAreas' && 'Bereiche hinzufügen'}
          {action === 'replaceSections' && 'Abschnitte ersetzen'}
          {action === 'addSections' && 'Abschnitte hinzufügen'}
        </h3>

        {action === 'points' && (
          <div>
            <label className="block text-sm font-medium mb-1">Neue Punktzahl</label>
            <input
              type="number"
              min="1"
              value={points}
              onChange={(e) => setPoints(parseInt(e.target.value) || 1)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        )}

        {(action === 'replaceAreas' || action === 'addAreas') && (
          <div>
            <label className="block text-sm font-medium mb-1">Bereiche wählen</label>
            <div className="border rounded p-2 max-h-48 overflow-y-auto">
              {areas.map(a => (
                <label key={a.id} className="flex items-center gap-2 p-1 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedAreas.includes(a.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAreas([...selectedAreas, a.id]);
                      } else {
                        setSelectedAreas(selectedAreas.filter(id => id !== a.id));
                      }
                    }}
                  />
                  {a.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {(action === 'replaceSections' || action === 'addSections') && (
          <div>
            <label className="block text-sm font-medium mb-1">Abschnitte wählen</label>
            <div className="border rounded p-2 max-h-48 overflow-y-auto">
              {sections.map(s => (
                <label key={s.id} className="flex items-center gap-2 p-1 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedSections.includes(s.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSections([...selectedSections, s.id]);
                      } else {
                        setSelectedSections(selectedSections.filter(id => id !== s.id));
                      }
                    }}
                  />
                  {s.name} ({s.area_name})
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// Question Form Modal
function QuestionForm({ question, areas, sections, onClose, onSave, showMessage }) {
  const [formData, setFormData] = useState({
    question_text: question?.question_text || '',
    question_type: question?.question_type || 'single_choice',
    points: question?.points || 1,
    areas: question?.areas?.map(a => a.id) || [],
    sections: question?.sections?.map(s => s.id) || [],
    answer_options: question?.answer_options || [{ option_text: '', is_correct: false }]
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let savedQuestion;
      if (question) {
        const response = await axios.put(`${API_URL}/questions/${question.id}`, formData);
        savedQuestion = response.data;
      } else {
        const response = await axios.post(`${API_URL}/questions`, formData);
        savedQuestion = response.data;
      }
      onSave(savedQuestion);
    } catch (error) {
      showMessage('Fehler beim Speichern', 'error');
    }
  };

  const addOption = () => {
    setFormData({
      ...formData,
      answer_options: [...formData.answer_options, { option_text: '', is_correct: false }]
    });
  };

  const removeOption = (index) => {
    setFormData({
      ...formData,
      answer_options: formData.answer_options.filter((_, i) => i !== index)
    });
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...formData.answer_options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, answer_options: newOptions });
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={(e) => { e.stopPropagation(); }}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <h3 className="text-xl font-bold mb-4">{question ? 'Frage bearbeiten' : 'Neue Frage'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Fragetext</label>
                <textarea
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 h-24"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Fragetyp</label>
                  <select
                    value={formData.question_type}
                    onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="single_choice">Einfachauswahl</option>
                    <option value="multiple_choice">Mehrfachauswahl</option>
                    <option value="free_text">Freitext</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Punkte</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.points}
                    onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Bereiche</label>
                <div className="flex flex-wrap gap-2">
                  {areas.map(area => (
                    <label key={area.id} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={formData.areas.includes(area.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, areas: [...formData.areas, area.id] });
                          } else {
                            // Bei Abwahl eines Bereichs auch die zugehörigen Abschnitte entfernen
                            const sectionIdsToRemove = sections.filter(s => s.area_id === area.id).map(s => s.id);
                            setFormData({ 
                              ...formData, 
                              areas: formData.areas.filter(id => id !== area.id),
                              sections: formData.sections.filter(id => !sectionIdsToRemove.includes(id))
                            });
                          }
                        }}
                      />
                      {area.name}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Abschnitte</label>
                {formData.areas.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Bitte zuerst einen Bereich auswählen</p>
                ) : (
                <div className="flex flex-wrap gap-2">
                  {sections.filter(s => formData.areas.includes(s.area_id)).map(section => (
                    <label key={section.id} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={formData.sections.includes(section.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, sections: [...formData.sections, section.id] });
                          } else {
                            setFormData({ ...formData, sections: formData.sections.filter(id => id !== section.id) });
                          }
                        }}
                      />
                      {section.name}
                    </label>
                  ))}
                </div>
                )}
              </div>

              {formData.question_type !== 'free_text' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Antwortoptionen</label>
                  {formData.answer_options.map((opt, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <textarea
                        value={opt.option_text}
                        onChange={(e) => updateOption(index, 'option_text', e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1 border rounded px-3 py-2 min-h-[60px] resize-y"
                        rows={2}
                      />
                      <label className="flex items-center gap-1 bg-green-50 px-2 rounded">
                        <input
                          type={formData.question_type === 'single_choice' ? 'radio' : 'checkbox'}
                          name="correct"
                          checked={opt.is_correct}
                          onChange={(e) => {
                            if (formData.question_type === 'single_choice') {
                              const newOptions = formData.answer_options.map((o, i) => ({
                                ...o,
                                is_correct: i === index
                              }));
                              setFormData({ ...formData, answer_options: newOptions });
                            } else {
                              updateOption(index, 'is_correct', e.target.checked);
                            }
                          }}
                        />
                        Richtig
                      </label>
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="text-red-600 px-2"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOption}
                    className="text-blue-600 text-sm"
                  >
                    + Option hinzufügen
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Areas Tab
function AreasTab({ areas, sections, onReload, showMessage }) {
  const [areaName, setAreaName] = useState('');
  const [areaDesc, setAreaDesc] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [sectionDesc, setSectionDesc] = useState('');
  const [selectedArea, setSelectedArea] = useState('');

  const handleAddArea = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/areas`, { name: areaName, description: areaDesc });
      setAreaName('');
      setAreaDesc('');
      showMessage('Bereich erstellt');
      onReload();
    } catch (error) {
      showMessage('Fehler beim Erstellen', 'error');
    }
  };

  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!selectedArea) {
      showMessage('Bitte Bereich auswählen', 'error');
      return;
    }
    try {
      await axios.post(`${API_URL}/sections`, { 
        name: sectionName, 
        description: sectionDesc,
        area_id: parseInt(selectedArea)
      });
      setSectionName('');
      setSectionDesc('');
      showMessage('Abschnitt erstellt');
      onReload();
    } catch (error) {
      showMessage('Fehler beim Erstellen', 'error');
    }
  };

  const handleDeleteArea = async (id) => {
    if (!window.confirm('Bereich und alle zugehörigen Abschnitte löschen?')) return;
    try {
      await axios.delete(`${API_URL}/areas/${id}`);
      showMessage('Bereich gelöscht');
      onReload();
    } catch (error) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm('Abschnitt wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/sections/${id}`);
      showMessage('Abschnitt gelöscht');
      onReload();
    } catch (error) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Bereiche & Abschnitte verwalten</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Areas */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Bereiche</h3>
          
          <form onSubmit={handleAddArea} className="mb-4 space-y-2">
            <input
              type="text"
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
              placeholder="Bereichsname"
              className="w-full border rounded px-3 py-2"
              required
            />
            <input
              type="text"
              value={areaDesc}
              onChange={(e) => setAreaDesc(e.target.value)}
              placeholder="Beschreibung (optional)"
              className="w-full border rounded px-3 py-2"
            />
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
            >
              + Bereich hinzufügen
            </button>
          </form>

          <div className="space-y-2">
            {areas.map(area => (
              <div key={area.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">{area.name}</p>
                  {area.description && <p className="text-sm text-gray-500">{area.description}</p>}
                </div>
                <button
                  onClick={() => handleDeleteArea(area.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Abschnitte</h3>
          
          <form onSubmit={handleAddSection} className="mb-4 space-y-2">
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Bereich auswählen...</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input
              type="text"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              placeholder="Abschnittsname"
              className="w-full border rounded px-3 py-2"
              required
            />
            <input
              type="text"
              value={sectionDesc}
              onChange={(e) => setSectionDesc(e.target.value)}
              placeholder="Beschreibung (optional)"
              className="w-full border rounded px-3 py-2"
            />
            <button
              type="submit"
              className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
            >
              + Abschnitt hinzufügen
            </button>
          </form>

          <div className="space-y-2">
            {sections.map(section => (
              <div key={section.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">{section.name}</p>
                  <p className="text-sm text-gray-500">Bereich: {section.area_name}</p>
                </div>
                <button
                  onClick={() => handleDeleteSection(section.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Exams Tab
function ExamsTab({ exams, areas, sections, questions, onReload, showMessage, pendingItem, onConsumeItem }) {
  const [showForm, setShowForm] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [preselectedQuestions, setPreselectedQuestions] = useState([]);
  const [preselectedTitle, setPreselectedTitle] = useState('');
  const [preselectedDescription, setPreselectedDescription] = useState('');
  const [selectedExam, setSelectedExam] = useState(null);
  const [publicLink, setPublicLink] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [correctorName, setCorrectorName] = useState('');
  const [previewExam, setPreviewExam] = useState(null);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingQuestionFromSubmission, setEditingQuestionFromSubmission] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    selections: [{ area_id: '', section_id: '', count: 5 }]
  });

  useEffect(() => {
    if (pendingItem?.type === 'exam') {
      const exam = exams.find(e => e.id === pendingItem.id);
      if (exam) {
        openExamDetails(exam);
      }
      onConsumeItem();
    } else if (pendingItem?.type === 'submission') {
      // Open exam details first, then the submission
      const exam = exams.find(e => e.id === pendingItem.examId);
      if (exam) {
        openExamDetails(exam).then(() => {
          loadSubmissionDetail(pendingItem.id);
        });
      }
      onConsumeItem();
    }
  }, [pendingItem]);

  const handleRandomGenerate = (e) => {
    e.preventDefault();
    
    // Sammle zufällige Fragen basierend auf den Auswahlkriterien
    let selectedQuestions = [];
    
    for (const selection of formData.selections) {
      const areaId = selection.area_id ? parseInt(selection.area_id) : null;
      const sectionId = selection.section_id ? parseInt(selection.section_id) : null;
      const count = parseInt(selection.count) || 0;
      
      // Filtere verfügbare Fragen
      let available = questions.filter(q => {
        // Bereits ausgewählt?
        if (selectedQuestions.find(sq => sq.id === q.id)) return false;
        // Bereichsfilter
        if (areaId && !q.areas.some(a => a.id === areaId)) return false;
        // Abschnittsfilter
        if (sectionId && !q.sections.some(s => s.id === sectionId)) return false;
        return true;
      });
      
      // Zufällig mischen und auswählen
      const shuffled = available.sort(() => Math.random() - 0.5);
      selectedQuestions = [...selectedQuestions, ...shuffled.slice(0, count)];
    }
    
    if (selectedQuestions.length === 0) {
      showMessage('Keine Fragen gefunden für die gewählten Kriterien', 'error');
      return;
    }
    
    // Öffne manuellen Editor mit vorausgewählten Fragen
    setPreselectedQuestions(selectedQuestions);
    setPreselectedTitle(formData.title);
    setPreselectedDescription(formData.description);
    setShowForm(false);
    setShowManualForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Prüfungsbogen wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/exams/${id}`);
      showMessage('Prüfungsbogen gelöscht');
      onReload();
    } catch (error) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await axios.post(`${API_URL}/exams/${id}/duplicate`);
      const duplicatedExam = response.data;
      showMessage('Prüfungsbogen dupliziert - Bearbeitung geöffnet');
      
      // Open the manual editor with the duplicated exam's questions
      const examQuestions = duplicatedExam.questions || [];
      const fullQuestions = examQuestions.map(eq => {
        const fullQ = questions.find(q => q.id === eq.id);
        return fullQ || eq;
      });
      
      setPreselectedQuestions(fullQuestions);
      setPreselectedTitle(duplicatedExam.title);
      setPreselectedDescription(duplicatedExam.description || '');
      setShowManualForm(true);
      // Don't call onReload here - it resets the state before modal shows
    } catch (error) {
      showMessage('Fehler beim Duplizieren', 'error');
    }
  };

  const openPreview = async (exam) => {
    try {
      const response = await axios.get(`${API_URL}/exams/${exam.id}/questions`);
      setPreviewQuestions(response.data);
      setPreviewExam(exam);
    } catch (error) {
      showMessage('Fehler beim Laden der Vorschau', 'error');
    }
  };

  const openManualForm = () => {
    setPreselectedQuestions([]);
    setPreselectedTitle('');
    setPreselectedDescription('');
    setShowManualForm(true);
  };

  const addSelection = () => {
    setFormData({
      ...formData,
      selections: [...formData.selections, { area_id: '', section_id: '', count: 5 }]
    });
  };

  const updateSelection = (index, field, value) => {
    const newSelections = [...formData.selections];
    newSelections[index] = { ...newSelections[index], [field]: value };
    setFormData({ ...formData, selections: newSelections });
  };

  const removeSelection = (index) => {
    setFormData({
      ...formData,
      selections: formData.selections.filter((_, i) => i !== index)
    });
  };

  // Public Link Functions
  const loadPublicLink = async (examId) => {
    try {
      const response = await axios.get(`${API_URL}/exams/${examId}/public-link`);
      setPublicLink(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        showMessage('Fehler beim Laden des Links', 'error');
      }
      setPublicLink(null);
    }
  };

  const createPublicLink = async (examId) => {
    try {
      const response = await axios.post(`${API_URL}/exams/${examId}/public-link`);
      setPublicLink(response.data);
      showMessage('Öffentlicher Link erstellt');
    } catch (error) {
      showMessage('Fehler beim Erstellen des Links', 'error');
    }
  };

  const togglePublicLink = async (examId, isActive) => {
    try {
      await axios.patch(`${API_URL}/exams/${examId}/public-link`, { is_active: isActive });
      setPublicLink({ ...publicLink, is_active: isActive });
      showMessage(isActive ? 'Link aktiviert' : 'Link deaktiviert');
    } catch (error) {
      showMessage('Fehler beim Aktualisieren', 'error');
    }
  };

  const loadSubmissions = async (examId) => {
    try {
      const response = await axios.get(`${API_URL}/exams/${examId}/submissions`);
      setSubmissions(response.data);
    } catch (error) {
      showMessage('Fehler beim Laden der Rückläufer', 'error');
    }
  };

  const openExamDetails = async (exam) => {
    setSelectedExam(exam);
    await loadPublicLink(exam.id);
    await loadSubmissions(exam.id);
    setShowSubmissions(true);
  };

  const loadSubmissionDetail = async (submissionId) => {
    try {
      const response = await axios.get(`${API_URL}/submissions/${submissionId}`);
      setSelectedSubmission(response.data);
    } catch (error) {
      showMessage('Fehler beim Laden', 'error');
    }
  };

  const gradeAnswer = async (answerId, isCorrect, points) => {
    try {
      await axios.patch(`${API_URL}/submissions/${selectedSubmission.id}/answers/${answerId}`, {
        is_correct: isCorrect,
        points_awarded: points
      });
      // Reload submission
      await loadSubmissionDetail(selectedSubmission.id);
      showMessage('Bewertung gespeichert');
    } catch (error) {
      showMessage('Fehler beim Speichern', 'error');
    }
  };

  const markAsCorrected = async () => {
    try {
      await axios.patch(`${API_URL}/submissions/${selectedSubmission.id}/corrected`, {
        corrector_name: correctorName
      });
      await loadSubmissionDetail(selectedSubmission.id);
      await loadSubmissions(selectedExam.id);
      setCorrectorName('');
      showMessage('Als korrigiert markiert');
    } catch (error) {
      showMessage('Fehler beim Markieren', 'error');
    }
  };

  const getPublicUrl = () => {
    if (!publicLink) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/pruefung/${publicLink.token}`;
  };

  const copyPublicLink = () => {
    navigator.clipboard.writeText(getPublicUrl());
    showMessage('Link kopiert');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Prüfungsbögen</h2>
        <div className="flex gap-2">
          <button
            onClick={openManualForm}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            ✋ Manuell erstellen
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            🎲 Zufällig erstellen
          </button>
        </div>
      </div>

      {/* Exams List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {exams.map(exam => (
          <div key={exam.id} className="bg-white rounded-lg shadow p-6">
            <div 
              className="cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded transition-colors"
              onClick={() => openPreview(exam)}
            >
              <h3 className="font-semibold text-lg mb-2">{exam.title}</h3>
              {exam.description && <p className="text-gray-600 text-sm mb-3">{exam.description}</p>}
              <p className="text-sm text-gray-500 mb-2">{exam.question_count} Fragen</p>
              <p className="text-xs text-blue-500 mb-2">👁️ Klicken für Vorschau</p>
            </div>
            <div className="flex gap-2 mb-2 mt-2">
              <a
                href={`${API_URL}/exams/${exam.id}/pdf?token=${localStorage.getItem('authToken')}`}
                className="flex-1 bg-blue-600 text-white text-center py-2 rounded hover:bg-blue-700"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                📄 PDF
              </a>
              <a
                href={`${API_URL}/exams/${exam.id}/pdf?solution=true&token=${localStorage.getItem('authToken')}`}
                className="flex-1 bg-green-600 text-white text-center py-2 rounded hover:bg-green-700"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                ✅ Lösung
              </a>
              <button
                onClick={() => handleDuplicate(exam.id)}
                className="bg-yellow-500 text-white px-3 py-2 rounded hover:bg-yellow-600"
                title="Duplizieren"
              >
                📋
              </button>
              <button
                onClick={() => handleDelete(exam.id)}
                className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700"
              >
                🗑️
              </button>
            </div>
            <button
              onClick={() => openExamDetails(exam)}
              className="w-full bg-purple-600 text-white py-2 rounded hover:bg-purple-700"
            >
              🔗 Link & Rückläufer
            </button>
          </div>
        ))}
      </div>

      {/* Submissions Modal */}
      {showSubmissions && selectedExam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">{selectedExam.title}</h3>
                <button onClick={() => { setShowSubmissions(false); setSelectedExam(null); setPublicLink(null); setSelectedSubmission(null); }} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
              </div>

              {/* Public Link Section */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold mb-3">🔗 Öffentlicher Link</h4>
                {!publicLink ? (
                  <button
                    onClick={() => createPublicLink(selectedExam.id)}
                    className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                  >
                    Link erstellen
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={getPublicUrl()}
                        className="flex-1 border rounded px-3 py-2 bg-white text-sm"
                      />
                      <button
                        onClick={copyPublicLink}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        📋 Kopieren
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={publicLink.is_active}
                          onChange={(e) => togglePublicLink(selectedExam.id, e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span>Link aktiv</span>
                      </label>
                      <span className="text-sm text-gray-500">
                        Erstellt: {new Date(publicLink.created_at).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Submissions Section */}
              <div>
                <h4 className="font-semibold mb-3">📥 Rückläufer ({submissions.length})</h4>
                {submissions.length === 0 ? (
                  <p className="text-gray-500">Noch keine Rückläufer vorhanden.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Eingereicht</th>
                          <th className="text-left p-2">Punkte</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions.map(sub => (
                          <tr key={sub.id} className="border-b hover:bg-gray-50">
                            <td className="p-2">{sub.participant_name || 'Anonym'}</td>
                            <td className="p-2">{new Date(sub.submitted_at).toLocaleString('de-DE')}</td>
                            <td className="p-2">{sub.total_points} / {sub.max_points}</td>
                            <td className="p-2">
                              {sub.is_corrected ? (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Korrigiert</span>
                              ) : (
                                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">Offen</span>
                              )}
                            </td>
                            <td className="p-2 flex gap-2">
                              <button
                                onClick={() => loadSubmissionDetail(sub.id)}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                              >
                                Ansehen
                              </button>
                              {sub.is_corrected && (
                                <a
                                  href={`${API_URL}/submissions/${sub.id}/pdf?token=${localStorage.getItem('authToken')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                                >
                                  PDF
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submission Detail Modal - Prüfungsähnliche Ansicht */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] m-4 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold">{selectedSubmission.exam_title}</h3>
                <p className="text-gray-600 text-sm">Rückläufer von: <strong>{selectedSubmission.participant_name || 'Anonym'}</strong></p>
              </div>
              <button onClick={() => setSelectedSubmission(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            {/* Score Summary */}
            <div className="p-4 border-b bg-gray-50 flex-shrink-0">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">{selectedSubmission.total_points} / {selectedSubmission.max_points}</div>
                    <div className="text-sm text-gray-500">Punkte</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${
                      selectedSubmission.max_points > 0 && (selectedSubmission.total_points / selectedSubmission.max_points) >= 0.5 
                        ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {selectedSubmission.max_points > 0 ? Math.round((selectedSubmission.total_points / selectedSubmission.max_points) * 100) : 0}%
                    </div>
                    <div className="text-sm text-gray-500">Ergebnis</div>
                  </div>
                  <div>
                    {selectedSubmission.max_points > 0 && (selectedSubmission.total_points / selectedSubmission.max_points) >= 0.5 ? (
                      <span className="bg-green-100 text-green-800 px-4 py-2 rounded-lg text-lg font-bold">✓ BESTANDEN</span>
                    ) : (
                      <span className="bg-red-100 text-red-800 px-4 py-2 rounded-lg text-lg font-bold">✗ NICHT BESTANDEN</span>
                    )}
                  </div>
                </div>
                <div>
                  {selectedSubmission.is_corrected ? (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm">✓ Korrigiert</span>
                  ) : (
                    <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded text-sm">⏳ Offen</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Questions */}
            <div className="flex-1 overflow-y-auto p-4">
              {selectedSubmission.answers?.map((answer, idx) => {
                const selectedTexts = answer.selected_options || [];
                const options = answer.answer_options || [];
                
                return (
                  <div key={answer.id} className="mb-6 p-4 border rounded-lg bg-gray-50">
                    {/* Question Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <span className={`text-white text-sm font-bold px-3 py-1 rounded-full ${
                        answer.is_correct === true ? 'bg-green-600' :
                        answer.is_correct === false ? 'bg-red-600' :
                        'bg-gray-500'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-lg">{answer.question_text}</p>
                        <div className="flex gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            answer.question_type === 'multiple_choice' ? 'bg-purple-100 text-purple-700' :
                            answer.question_type === 'single_choice' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {answer.question_type === 'multiple_choice' ? 'Multiple Choice' :
                             answer.question_type === 'single_choice' ? 'Single Choice' : 'Freitext'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            answer.is_correct === true ? 'bg-green-100 text-green-800' :
                            answer.is_correct === false ? 'bg-red-100 text-red-800' :
                            'bg-gray-200 text-gray-700'
                          }`}>
                            {answer.points_awarded} / {answer.max_points} Punkte
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const fullQuestion = questions.find(q => q.id === answer.question_id);
                            if (fullQuestion) {
                              setEditingQuestionFromSubmission(fullQuestion);
                            }
                          }}
                          className="text-gray-500 hover:text-blue-600 p-1"
                          title="Frage bearbeiten"
                        >
                          ✏️
                        </button>
                        <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                          answer.is_correct === true ? 'bg-green-100 text-green-800' :
                          answer.is_correct === false ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {answer.is_correct === true ? '✓ Richtig' :
                           answer.is_correct === false ? '✗ Falsch' :
                           '? Offen'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Answer Options */}
                    {answer.question_type !== 'free_text' && options.length > 0 ? (
                      <div className="ml-10 space-y-2">
                        {options.map((option, optIndex) => {
                          const isSelected = selectedTexts.includes(option.option_text);
                          const isCorrect = option.is_correct;
                          
                          // Determine styling based on selection and correctness
                          let bgColor = 'bg-white border-gray-200';
                          let textColor = 'text-gray-700';
                          let icon = '';
                          
                          if (isSelected && isCorrect) {
                            // Correctly selected
                            bgColor = 'bg-green-100 border-green-400';
                            textColor = 'text-green-800';
                            icon = '✓';
                          } else if (isSelected && !isCorrect) {
                            // Wrongly selected
                            bgColor = 'bg-red-100 border-red-400';
                            textColor = 'text-red-800';
                            icon = '✗';
                          } else if (!isSelected && isCorrect) {
                            // Should have been selected (missed)
                            bgColor = 'bg-yellow-50 border-yellow-400';
                            textColor = 'text-yellow-800';
                            icon = '!';
                          }
                          
                          return (
                            <div 
                              key={option.id} 
                              className={`flex items-center gap-2 p-3 rounded-lg border-2 ${bgColor}`}
                            >
                              <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold border-2 ${
                                isSelected 
                                  ? (isCorrect ? 'border-green-600 bg-green-600 text-white' : 'border-red-600 bg-red-600 text-white')
                                  : (isCorrect ? 'border-yellow-500 bg-yellow-100 text-yellow-700' : 'border-gray-300 text-gray-500')
                              }`}>
                                {icon || String.fromCharCode(65 + optIndex)}
                              </span>
                              <span className={`flex-1 ${textColor} ${isCorrect ? 'font-medium' : ''}`}>
                                {option.option_text}
                              </span>
                              <div className="flex gap-2 text-xs">
                                {isSelected && (
                                  <span className={`px-2 py-0.5 rounded ${isCorrect ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                                    Gewählt
                                  </span>
                                )}
                                {isCorrect && (
                                  <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                                    Musterlösung
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : answer.question_type === 'free_text' ? (
                      <div className="ml-10">
                        <div className="p-3 bg-white border border-gray-300 rounded-lg mb-3">
                          <p className="text-sm text-gray-500 mb-1">Gegebene Antwort:</p>
                          <p className="whitespace-pre-wrap">{answer.answer_text || <span className="text-gray-400 italic">(keine Antwort)</span>}</p>
                        </div>
                        
                        {/* Manual grading for free text */}
                        <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
                          <span className="text-sm font-medium">Bewertung:</span>
                          <button
                            onClick={() => gradeAnswer(answer.id, true, answer.max_points)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              answer.is_correct === true 
                                ? 'bg-green-600 text-white' 
                                : 'bg-white border border-green-300 text-green-700 hover:bg-green-50'
                            }`}
                          >
                            ✓ Richtig ({answer.max_points} Pkt)
                          </button>
                          <button
                            onClick={() => gradeAnswer(answer.id, false, 0)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              answer.is_correct === false 
                                ? 'bg-red-600 text-white' 
                                : 'bg-white border border-red-300 text-red-700 hover:bg-red-50'
                            }`}
                          >
                            ✗ Falsch (0 Pkt)
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="ml-10 p-3 bg-white border border-dashed border-gray-300 rounded-lg text-gray-400 italic">
                        Keine Antwortoptionen verfügbar
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t flex justify-between items-center flex-shrink-0 bg-gray-50">
              {!selectedSubmission.is_corrected ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Name des Korrektors"
                    value={correctorName}
                    onChange={(e) => setCorrectorName(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    onClick={markAsCorrected}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium"
                  >
                    ✓ Als korrigiert markieren
                  </button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  Korrigiert von: {selectedSubmission.corrector_name || 'Unbekannt'}
                </div>
              )}
              <div className="flex gap-2">
                <a
                  href={`${API_URL}/submissions/${selectedSubmission.id}/pdf?token=${localStorage.getItem('authToken')}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📄 PDF herunterladen
                </a>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Question Edit Modal from Submission View */}
      {editingQuestionFromSubmission && (
        <QuestionForm
          question={editingQuestionFromSubmission}
          areas={areas}
          sections={sections}
          onClose={() => setEditingQuestionFromSubmission(null)}
          onSave={async () => {
            setEditingQuestionFromSubmission(null);
            onReload();
            // Refresh submission data
            if (selectedSubmission) {
              try {
                const response = await axios.get(`${API_URL}/submissions/${selectedSubmission.id}`);
                setSelectedSubmission(response.data);
              } catch (e) {}
            }
            showMessage('Frage aktualisiert');
          }}
          showMessage={showMessage}
        />
      )}

      {/* Create Exam Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Zufällige Fragenauswahl</h3>
              <p className="text-sm text-gray-600 mb-4">Wählen Sie Kriterien für die zufällige Auswahl. Die Fragen werden danach im Editor angezeigt, wo Sie sie noch anpassen können.</p>
              <form onSubmit={handleRandomGenerate}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Titel</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Beschreibung</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Fragenauswahl</label>
                    {formData.selections.map((sel, index) => (
                      <div key={index} className="flex gap-2 mb-2 items-center">
                        <select
                          value={sel.area_id}
                          onChange={(e) => updateSelection(index, 'area_id', e.target.value)}
                          className="flex-1 border rounded px-3 py-2"
                        >
                          <option value="">Alle Bereiche</option>
                          {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                        <select
                          value={sel.section_id}
                          onChange={(e) => updateSelection(index, 'section_id', e.target.value)}
                          className="flex-1 border rounded px-3 py-2"
                        >
                          <option value="">Alle Abschnitte</option>
                          {sections
                            .filter(s => !sel.area_id || s.area_id === parseInt(sel.area_id))
                            .map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                          }
                        </select>
                        <input
                          type="number"
                          min="1"
                          value={sel.count}
                          onChange={(e) => updateSelection(index, 'count', e.target.value)}
                          className="w-20 border rounded px-3 py-2"
                          placeholder="Anzahl"
                        />
                        {formData.selections.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSelection(index)}
                            className="text-red-600 px-2"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addSelection}
                      className="text-blue-600 text-sm"
                    >
                      + Weitere Auswahl hinzufügen
                    </button>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    🎲 Fragen auswählen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Manual Exam Creation Modal */}
      {showManualForm && (
        <ManualExamBuilder
          questions={questions}
          areas={areas}
          sections={sections}
          initialQuestions={preselectedQuestions}
          initialTitle={preselectedTitle}
          initialDescription={preselectedDescription}
          onClose={() => { setShowManualForm(false); setPreselectedQuestions([]); }}
          onSave={() => { setShowManualForm(false); setPreselectedQuestions([]); onReload(); showMessage('Prüfungsbogen erstellt'); }}
          showMessage={showMessage}
          onReloadQuestions={onReload}
        />
      )}

      {/* Question Edit Modal */}
      {editingQuestion && (
        <QuestionForm
          question={editingQuestion}
          areas={areas}
          sections={sections}
          onClose={() => setEditingQuestion(null)}
          onSave={async () => {
            setEditingQuestion(null);
            // Reload questions and refresh preview if open
            onReload();
            if (previewExam) {
              try {
                const response = await axios.get(`${API_URL}/exams/${previewExam.id}/questions`);
                setPreviewQuestions(response.data);
              } catch (e) {}
            }
            showMessage('Frage aktualisiert');
          }}
          showMessage={showMessage}
        />
      )}

      {/* Exam Preview Modal */}
      {previewExam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] m-4 flex flex-col">
            <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold">{previewExam.title}</h3>
                {previewExam.description && <p className="text-gray-600 text-sm">{previewExam.description}</p>}
              </div>
              <button onClick={() => setPreviewExam(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{previewQuestions.length} Fragen</strong> | 
                  Gesamtpunkte: <strong>{previewQuestions.reduce((sum, q) => sum + q.points, 0)}</strong>
                </p>
              </div>
              
              {previewQuestions.map((question, index) => (
                <div key={question.id} className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-lg">{question.question_text}</p>
                      <div className="flex gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          question.question_type === 'multiple_choice' ? 'bg-purple-100 text-purple-700' :
                          question.question_type === 'single_choice' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {question.question_type === 'multiple_choice' ? 'Multiple Choice' :
                           question.question_type === 'single_choice' ? 'Single Choice' : 'Freitext'}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700">
                          {question.points} Punkt{question.points !== 1 ? 'e' : ''}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setEditingQuestion(question)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Frage bearbeiten"
                    >
                      ✏️
                    </button>
                  </div>
                  
                  {question.question_type !== 'free_text' && question.answer_options && (
                    <div className="ml-10 space-y-2">
                      {question.answer_options.map((option, optIndex) => (
                        <div 
                          key={option.id} 
                          className={`flex items-center gap-2 p-2 rounded ${
                            option.is_correct ? 'bg-green-100 border border-green-300' : 'bg-white border border-gray-200'
                          }`}
                        >
                          <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium ${
                            question.question_type === 'single_choice' ? 'border-2' : 'border-2 rounded'
                          } ${option.is_correct ? 'border-green-600 bg-green-600 text-white' : 'border-gray-300 text-gray-500'}`}>
                            {String.fromCharCode(65 + optIndex)}
                          </span>
                          <span className={option.is_correct ? 'font-medium text-green-800' : 'text-gray-700'}>
                            {option.option_text}
                          </span>
                          {option.is_correct && (
                            <span className="ml-auto text-green-600 text-sm">✓ Richtig</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {question.question_type === 'free_text' && (
                    <div className="ml-10 p-3 bg-white border border-dashed border-gray-300 rounded text-gray-400 italic">
                      Freitext-Antwort (wird manuell bewertet)
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="p-4 border-t flex justify-end gap-2 flex-shrink-0 bg-gray-50">
              <a
                href={`${API_URL}/exams/${previewExam.id}/pdf?token=${localStorage.getItem('authToken')}`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                target="_blank"
                rel="noopener noreferrer"
              >
                📄 PDF herunterladen
              </a>
              <a
                href={`${API_URL}/exams/${previewExam.id}/pdf?solution=true&token=${localStorage.getItem('authToken')}`}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                target="_blank"
                rel="noopener noreferrer"
              >
                ✅ Lösungsbogen
              </a>
              <button
                onClick={() => setPreviewExam(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Manual Exam Builder with Drag and Drop
function ManualExamBuilder({ questions, areas, sections, initialQuestions = [], initialTitle = '', initialDescription = '', onClose, onSave, showMessage, onReloadQuestions }) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [passingPercentage, setPassingPercentage] = useState(50);
  const [selectedQuestions, setSelectedQuestions] = useState(initialQuestions);
  const [filterArea, setFilterArea] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedQuestion, setDraggedQuestion] = useState(null);
  const [localEditingQuestion, setLocalEditingQuestion] = useState(null);

  // Filter available questions (exclude already selected)
  const availableQuestions = questions.filter(q => {
    if (selectedQuestions.find(sq => sq.id === q.id)) return false;
    if (filterArea && !q.areas.some(a => a.id === parseInt(filterArea))) return false;
    if (filterSection && !q.sections.some(s => s.id === parseInt(filterSection))) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesText = q.question_text.toLowerCase().includes(query);
      const matchesOptions = q.answer_options?.some(o => o.option_text.toLowerCase().includes(query));
      if (!matchesText && !matchesOptions) return false;
    }
    return true;
  });

  const handleDragStart = (e, question, fromSelected = false) => {
    setDraggedQuestion({ ...question, fromSelected });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnExam = (e) => {
    e.preventDefault();
    if (draggedQuestion && !draggedQuestion.fromSelected) {
      setSelectedQuestions([...selectedQuestions, draggedQuestion]);
    }
    setDraggedQuestion(null);
  };

  const handleDropOnPool = (e) => {
    e.preventDefault();
    if (draggedQuestion && draggedQuestion.fromSelected) {
      setSelectedQuestions(selectedQuestions.filter(q => q.id !== draggedQuestion.id));
    }
    setDraggedQuestion(null);
  };

  const handleDropOnPosition = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedQuestion) return;

    if (draggedQuestion.fromSelected) {
      // Reorder within selected
      const currentIndex = selectedQuestions.findIndex(q => q.id === draggedQuestion.id);
      if (currentIndex === targetIndex) return;
      
      const newSelected = [...selectedQuestions];
      newSelected.splice(currentIndex, 1);
      newSelected.splice(targetIndex, 0, draggedQuestion);
      setSelectedQuestions(newSelected);
    } else {
      // Add from pool at specific position
      const newSelected = [...selectedQuestions];
      newSelected.splice(targetIndex, 0, draggedQuestion);
      setSelectedQuestions(newSelected);
    }
    setDraggedQuestion(null);
  };

  const removeFromExam = (questionId) => {
    setSelectedQuestions(selectedQuestions.filter(q => q.id !== questionId));
  };

  const moveQuestion = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= selectedQuestions.length) return;
    
    const newSelected = [...selectedQuestions];
    [newSelected[index], newSelected[newIndex]] = [newSelected[newIndex], newSelected[index]];
    setSelectedQuestions(newSelected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedQuestions.length === 0) {
      showMessage('Bitte mindestens eine Frage hinzufügen', 'error');
      return;
    }
    try {
      await axios.post(`${API_URL}/exams/manual`, {
        title,
        description,
        passing_percentage: passingPercentage,
        question_ids: selectedQuestions.map(q => q.id)
      });
      onSave();
    } catch (error) {
      showMessage('Fehler beim Erstellen', 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] m-4 flex flex-col">
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="text-xl font-bold">Prüfungsbogen manuell erstellen</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-4 border-b grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titel *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Beschreibung</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bestehensgrenze (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={passingPercentage}
                onChange={(e) => setPassingPercentage(Number(e.target.value))}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Left: Question Pool */}
            <div 
              className="w-1/2 border-r flex flex-col"
              onDragOver={handleDragOver}
              onDrop={handleDropOnPool}
            >
              <div className="p-3 bg-gray-50 border-b">
                <h4 className="font-semibold mb-2">📚 Fragenpool ({availableQuestions.length})</h4>
                <input
                  type="text"
                  placeholder="🔍 Suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm mb-2"
                />
                <div className="flex gap-2">
                  <select
                    value={filterArea}
                    onChange={(e) => { setFilterArea(e.target.value); setFilterSection(''); }}
                    className="flex-1 border rounded px-2 py-1 text-sm"
                  >
                    <option value="">Alle Bereiche</option>
                    {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  <select
                    value={filterSection}
                    onChange={(e) => setFilterSection(e.target.value)}
                    className="flex-1 border rounded px-2 py-1 text-sm"
                  >
                    <option value="">Alle Abschnitte</option>
                    {sections
                      .filter(s => !filterArea || s.area_id === parseInt(filterArea))
                      .map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                    }
                  </select>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {availableQuestions.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Keine Fragen verfügbar</p>
                ) : (
                  availableQuestions.map(q => (
                    <div
                      key={q.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, q, false)}
                      className="p-3 mb-2 bg-white border rounded-lg cursor-grab hover:shadow-md hover:border-blue-300 transition-all"
                    >
                      <p className="text-sm font-medium truncate">{q.question_text}</p>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          q.question_type === 'multiple_choice' ? 'bg-purple-100 text-purple-700' :
                          q.question_type === 'single_choice' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {q.question_type === 'multiple_choice' ? 'MC' :
                           q.question_type === 'single_choice' ? 'SC' : 'Text'}
                        </span>
                        {q.areas.map(a => (
                          <span key={a.id} className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{a.name}</span>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right: Exam Questions */}
            <div 
              className={`w-1/2 flex flex-col ${draggedQuestion && !draggedQuestion.fromSelected ? 'bg-green-50' : ''}`}
              onDragOver={handleDragOver}
              onDrop={handleDropOnExam}
            >
              <div className="p-3 bg-gray-50 border-b">
                <h4 className="font-semibold">📝 Prüfungsbogen ({selectedQuestions.length} Fragen)</h4>
                <p className="text-xs text-gray-500">Fragen hierher ziehen oder per Doppelklick hinzufügen</p>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {selectedQuestions.length === 0 ? (
                  <div className="h-full flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg m-2">
                    <p className="text-gray-400">Fragen hierher ziehen</p>
                  </div>
                ) : (
                  selectedQuestions.map((q, index) => (
                    <div
                      key={q.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, q, true)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDropOnPosition(e, index)}
                      className="p-3 mb-2 bg-white border-2 border-blue-200 rounded-lg cursor-grab hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-2">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{q.question_text}</p>
                          <div className="flex gap-1 mt-1">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              q.question_type === 'multiple_choice' ? 'bg-purple-100 text-purple-700' :
                              q.question_type === 'single_choice' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {q.question_type === 'multiple_choice' ? 'MC' :
                               q.question_type === 'single_choice' ? 'SC' : 'Text'}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => moveQuestion(index, -1)}
                            disabled={index === 0}
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            onClick={() => moveQuestion(index, 1)}
                            disabled={index === selectedQuestions.length - 1}
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            ▼
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setLocalEditingQuestion(q)}
                          className="text-gray-400 hover:text-blue-600"
                          title="Frage bearbeiten"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFromExam(q.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="p-4 border-t flex justify-end gap-2 flex-shrink-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={selectedQuestions.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Prüfungsbogen erstellen ({selectedQuestions.length} Fragen)
            </button>
          </div>
        </form>
      </div>

      {/* Local Question Edit Modal for ManualExamBuilder */}
      {localEditingQuestion && (
        <QuestionForm
          question={localEditingQuestion}
          areas={areas}
          sections={sections}
          onClose={() => setLocalEditingQuestion(null)}
          onSave={async (updatedQuestion) => {
            // Update the question in selectedQuestions list with fresh data
            if (updatedQuestion) {
              setSelectedQuestions(prev => prev.map(q => 
                q.id === updatedQuestion.id ? updatedQuestion : q
              ));
            }
            setLocalEditingQuestion(null);
            showMessage('Frage aktualisiert');
            // Reload questions in parent to keep in sync
            if (onReloadQuestions) {
              onReloadQuestions();
            }
          }}
          showMessage={showMessage}
        />
      )}
    </div>
  );
}

// Import Tab
function ImportTab({ onReload, showMessage }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleImport = async (e) => {
    e.preventDefault();
    if (!file) {
      showMessage('Bitte Datei auswählen', 'error');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/questions/import`, formData);
      showMessage(`${response.data.imported} Fragen importiert`);
      setFile(null);
      onReload();
    } catch (error) {
      showMessage('Fehler beim Import', 'error');
    }
    setImporting(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">CSV Import</h2>
      
      <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
        <h3 className="font-semibold mb-4">Fragen aus CSV importieren</h3>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-blue-800 mb-2">CSV Format</h4>
          <p className="text-sm text-blue-700 mb-2">Die CSV-Datei sollte folgende Spalten enthalten:</p>
          <ul className="text-sm text-blue-700 list-disc list-inside">
            <li><code>question_text</code> oder <code>Frage</code> - Der Fragetext</li>
            <li><code>question_type</code> oder <code>Typ</code> - single_choice, multiple_choice, free_text</li>
            <li><code>points</code> oder <code>Punkte</code> - Punktzahl</li>
            <li><code>areas</code> oder <code>Bereiche</code> - Bereiche getrennt durch ;</li>
            <li><code>sections</code> oder <code>Abschnitte</code> - Abschnitte getrennt durch ;</li>
            <li><code>options</code> oder <code>Optionen</code> - Antwortoptionen getrennt durch ;</li>
            <li><code>correct_options</code> oder <code>Korrekt</code> - Korrekte Optionen als Buchstaben (A;B;C) oder Nummern (1;2;3)</li>
          </ul>
        </div>

        <form onSubmit={handleImport}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">CSV-Datei auswählen</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <button
            type="submit"
            disabled={importing}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {importing ? 'Importiere...' : 'Importieren'}
          </button>
        </form>
      </div>

      {/* Example CSV Download */}
      <div className="mt-6 bg-white rounded-lg shadow p-6 max-w-2xl">
        <h3 className="font-semibold mb-4">Beispiel CSV</h3>
        <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`Frage,Typ,Punkte,Bereiche,Abschnitte,Optionen,Korrekt
"Was ist 2+2?",single_choice,1,Mathematik,Algebra,"2;3;4;5",C
"Welche Zahlen sind gerade?",multiple_choice,2,Mathematik,Algebra,"2;3;4;5",A;C
"Erklären Sie den Satz des Pythagoras.",free_text,3,Mathematik,Geometrie,,`}
        </pre>
        <p className="text-xs text-gray-500 mt-2">Hinweis: Bei "Korrekt" können Sie Buchstaben (A, B, C...) oder Nummern (1, 2, 3...) verwenden.</p>
      </div>
    </div>
  );
}

// Settings Tab Component
function SettingsTab({ showMessage }) {
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/logo`);
      if (response.data.logo) {
        setLogoPreview(response.data.logo);
      }
    } catch (error) {
      // No logo set, that's ok
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showMessage('Datei zu groß (max. 2MB)', 'error');
        return;
      }
      if (!file.type.startsWith('image/')) {
        showMessage('Nur Bilddateien erlaubt', 'error');
        return;
      }
      setLogo(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!logo) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          await axios.post(`${API_URL}/settings/logo`, {
            logo: e.target.result
          });
          showMessage('Logo gespeichert');
          setLogo(null);
        } catch (error) {
          showMessage('Fehler beim Speichern', 'error');
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(logo);
    } catch (error) {
      showMessage('Fehler beim Hochladen', 'error');
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Logo wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/settings/logo`);
      setLogoPreview(null);
      setLogo(null);
      showMessage('Logo gelöscht');
    } catch (error) {
      showMessage('Fehler beim Löschen', 'error');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Lade Einstellungen...</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">⚙️ Einstellungen</h2>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">📷 Logo / Briefkopf für PDFs</h3>
        <p className="text-gray-600 mb-4">
          Laden Sie ein Logo hoch, das auf allen PDF-Dokumenten (Prüfungsbögen und Korrekturen) angezeigt wird.
        </p>
        
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              {logoPreview ? (
                <div>
                  <img 
                    src={logoPreview} 
                    alt="Logo Preview" 
                    className="max-h-32 mx-auto mb-4 object-contain"
                  />
                  <p className="text-sm text-gray-500 mb-2">Aktuelles Logo</p>
                </div>
              ) : (
                <div className="text-gray-400 py-8">
                  <span className="text-4xl">📷</span>
                  <p className="mt-2">Kein Logo hochgeladen</p>
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="inline-block mt-4 bg-blue-600 text-white px-4 py-2 rounded cursor-pointer hover:bg-blue-700"
              >
                {logoPreview ? 'Neues Logo wählen' : 'Logo hochladen'}
              </label>
            </div>
            
            <div className="flex gap-2 mt-4">
              {logo && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                >
                  {uploading ? 'Speichert...' : '💾 Logo speichern'}
                </button>
              )}
              {logoPreview && !logo && (
                <button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                  🗑️ Logo löschen
                </button>
              )}
            </div>
          </div>
          
          <div className="flex-1 bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium mb-2">📋 Hinweise</h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Unterstützte Formate: PNG, JPG, GIF</li>
              <li>• Maximale Dateigröße: 2 MB</li>
              <li>• Empfohlene Größe: 300x100 Pixel</li>
              <li>• Das Logo wird oben links auf den PDFs angezeigt</li>
              <li>• Transparente PNGs werden unterstützt</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
